// @ts-check
// Pure helpers for drawing badges (#153), kept apart from the components so
// they can be tested without rendering.

/**
 * @typedef {{ key: string, family: string, category?: string, icon?: string,
 *   title: string, description: string, rank: string, tier?: number,
 *   unit?: string, secret?: boolean, limited?: boolean, earned_at: string|null,
 *   progress: { current: number, target: number }|null }} Badge
 */

// A medal's frame shape by category (#170), so a grid of hundreds scans.
const CATEGORY_SHAPES = {
  volume: "circle",
  time: "circle",
  consistency: "shield",
  programmes: "shield",
  distance: "hexagon",
  effort: "octagon",
  weather: "diamond",
  social: "square",
  oneoff: "rosette",
  challenges: "rosette",
};

/** @param {Badge} b */
export const medalShape = (b) => CATEGORY_SHAPES[b.category || ""] || "circle";

/**
 * A tier number short enough for the medal's ribbon: 2500 → "2.5k".
 * @param {number} n
 */
export const shortTier = (n) =>
  n >= 1000 ? `${Number((n / 1000).toFixed(1))}k` : String(n);

/**
 * Earned badges newest first; the rest closest to earning first, with the
 * one-offs (which have no progress to show) last.
 * @param {Badge[]|undefined} badges
 */
export const splitBadges = (badges) => {
  const list = Array.isArray(badges) ? badges : [];
  const earned = list
    .filter((b) => b.earned_at)
    .sort((a, b) => Date.parse(b.earned_at || "") - Date.parse(a.earned_at || ""));
  const share = (/** @type {Badge} */ b) => (b.progress ? b.progress.current / b.progress.target : -1);
  const locked = list.filter((b) => !b.earned_at).sort((a, b) => share(b) - share(a));
  return { earned, locked };
};

/** @param {string} iso @param {boolean} long */
const formatDate = (iso, long) => {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: long ? "long" : "short",
    ...(long || !sameYear ? { year: "numeric" } : {}),
  });
};

/**
 * The small line under a badge: when it was earned, or how far along.
 * @param {Badge} b
 */
export const badgeCaption = (b) => {
  if (b.earned_at) return formatDate(b.earned_at, false);
  if (b.progress) return `${b.progress.current}/${b.progress.target}`;
  return "Locked";
};

/**
 * The badges closest to being earned, for "Next up": started, not secret,
 * furthest along first.
 * @param {Badge[]|undefined} badges
 * @param {number} [count]
 */
export const nextUp = (badges, count = 3) =>
  (Array.isArray(badges) ? badges : [])
    .filter((b) => !b.earned_at && !b.secret && b.progress && b.progress.current > 0)
    .sort((a, b) => {
      const pa = /** @type {{ current: number, target: number }} */ (a.progress);
      const pb = /** @type {{ current: number, target: number }} */ (b.progress);
      return pb.current / pb.target - pa.current / pa.target;
    })
    .slice(0, count);

/**
 * Badges grouped by category in the server's order, empty groups dropped.
 * @param {Badge[]} badges
 * @param {{ key: string, title: string }[]|undefined} categories
 */
export const groupByCategory = (badges, categories) => {
  const order = Array.isArray(categories) && categories.length ? categories : [{ key: "", title: "Badges" }];
  return order
    .map((c) => ({ ...c, badges: badges.filter((b) => (b.category || "") === c.key || order.length === 1) }))
    .filter((g) => g.badges.length > 0);
};

/**
 * Of the badges still to earn, only each family's next tier, so 222 badges
 * don't bury the ones in reach. One-offs and secrets are kept as they are.
 * @param {Badge[]} locked
 */
export const nextTiers = (locked) => {
  const lowest = new Map();
  for (const b of locked) {
    if (b.tier == null) continue;
    const seen = lowest.get(b.family);
    if (!seen || b.tier < (seen.tier ?? Infinity)) lowest.set(b.family, b);
  }
  return locked.filter((b) => b.tier == null || lowest.get(b.family) === b);
};

/**
 * Which earned badges are new since the last look, given the keys already
 * seen. With no record yet (first look ever) nothing is new, so a long
 * history doesn't all pop at once.
 * @param {Badge[]} earned
 * @param {string[]|null} seen
 */
export const newlyEarned = (earned, seen) => {
  if (!Array.isArray(seen)) return new Set();
  const had = new Set(seen);
  return new Set(earned.filter((b) => !had.has(b.key)).map((b) => b.key));
};

/**
 * The same, as a sentence, for the detail panel and screen readers.
 * @param {Badge} b
 */
export const describeBadgeStatus = (b) => {
  if (b.earned_at) return `Earned ${formatDate(b.earned_at, true)}`;
  if (b.progress) {
    const unit = b.unit ? ` ${b.unit}` : "";
    return `${b.progress.current.toLocaleString("en-AU")} of ${b.progress.target.toLocaleString("en-AU")}${unit} so far`;
  }
  if (b.secret) return "Secret until you earn it";
  return "Not earned yet";
};

/**
 * @typedef {{ family: string, category?: string, top: Badge, tiers: Badge[],
 *   earned: Badge[], next: Badge|null }} BadgeFamily
 */

/**
 * Earned badges as one entry per family (#203): your highest tier on top,
 * every tier in order for the family's ladder, and the next one to earn.
 * Families in the order their top tier was earned, newest first.
 * @param {Badge[]|undefined} badges
 * @returns {BadgeFamily[]}
 */
export const earnedFamilies = (badges) => {
  /** @type {Map<string, Badge[]>} */
  const byFamily = new Map();
  for (const b of Array.isArray(badges) ? badges : []) {
    const list = byFamily.get(b.family) || [];
    list.push(b);
    byFamily.set(b.family, list);
  }
  /** @type {BadgeFamily[]} */
  const out = [];
  byFamily.forEach((list, family) => {
    const tiers = [...list].sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0));
    const earned = tiers.filter((b) => b.earned_at);
    if (earned.length === 0) return;
    out.push({
      family,
      category: tiers[0].category,
      top: earned[earned.length - 1],
      tiers,
      earned,
      next: tiers.find((b) => !b.earned_at) || null,
    });
  });
  return out.sort((a, b) => Date.parse(b.top.earned_at || "") - Date.parse(a.top.earned_at || ""));
};

/**
 * One family's tiers, earned or not, for its ladder (#203): `top` is null
 * until a tier is earned. Null when no badge has that family.
 * @param {Badge[]|undefined} badges
 * @param {string|null} family
 * @returns {null | { family: string, category?: string, top: Badge|null, tiers: Badge[], earned: Badge[], next: Badge|null }}
 */
export const familyOf = (badges, family) => {
  const tiers = (Array.isArray(badges) ? badges : [])
    .filter((b) => b.family === family)
    .sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0));
  if (!family || tiers.length === 0) return null;
  const earned = tiers.filter((b) => b.earned_at);
  return {
    family,
    category: tiers[0].category,
    top: earned[earned.length - 1] || null,
    tiers,
    earned,
    next: tiers.find((b) => !b.earned_at) || null,
  };
};
